import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { 
  Database, Search, Download, RefreshCw, Eye, Edit2, Trash2, X, Save,
  FileText, Users, Phone, Calendar, Settings, ClipboardList, ArrowLeft,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const DataViewer = () => {
  const [activeCollection, setActiveCollection] = useState('estimates');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [editedData, setEditedData] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 20;

  const collections = [
    { id: 'estimates', label: 'Leads/Estimates', icon: FileText, endpoint: '/api/estimates', color: 'bg-amber-500' },
    { id: 'clients', label: 'Clients', icon: Users, endpoint: '/api/clients', color: 'bg-purple-500' },
    { id: 'contacts', label: 'Contacts', icon: Phone, endpoint: '/api/contacts', color: 'bg-blue-500' },
    { id: 'tasks', label: 'Calendar Tasks', icon: Calendar, endpoint: '/api/tasks', color: 'bg-green-500' },
    { id: 'templates', label: 'Templates', icon: ClipboardList, endpoint: '/api/templates', color: 'bg-orange-500' },
    { id: 'settings', label: 'Settings', icon: Settings, endpoint: '/api/settings', color: 'bg-gray-500', single: true },
  ];

  const fetchData = async () => {
    setLoading(true);
    try {
      const collection = collections.find(c => c.id === activeCollection);
      const response = await fetch(`${BACKEND_URL}${collection.endpoint}`);
      if (response.ok) {
        const result = await response.json();
        // Settings returns a single object, wrap it in array
        setData(collection.single ? [result] : (Array.isArray(result) ? result : []));
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error loading data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    setCurrentPage(1);
    setSelectedRecord(null);
    setEditingRecord(null);
  }, [activeCollection]);

  // Filter data based on search
  const filteredData = data.filter(record => {
    if (!searchQuery) return true;
    const searchLower = searchQuery.toLowerCase();
    return JSON.stringify(record).toLowerCase().includes(searchLower);
  });

  // Pagination
  const totalPages = Math.ceil(filteredData.length / recordsPerPage);
  const paginatedData = filteredData.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  // Get display fields for each collection
  const getDisplayFields = (record) => {
    switch (activeCollection) {
      case 'estimates':
        return {
          primary: record.client_name || 'Unnamed',
          secondary: record.proposal_id || record.id?.slice(0, 8),
          badge: record.lead_status,
          extra: `$${(record.grand_total || 0).toLocaleString()}`
        };
      case 'clients':
        return {
          primary: record.name || 'Unnamed',
          secondary: record.email || record.phone,
          badge: record.status,
          extra: record.address?.slice(0, 30)
        };
      case 'contacts':
        return {
          primary: record.name || 'Unnamed',
          secondary: record.email || record.phone,
          badge: record.role,
          extra: record.company
        };
      case 'tasks':
        return {
          primary: record.title || 'Untitled',
          secondary: record.start?.slice(0, 10),
          badge: record.type,
          extra: record.status
        };
      case 'templates':
        return {
          primary: record.category || record.name || 'Unnamed',
          secondary: record.description?.slice(0, 40),
          badge: record.unit,
          extra: `$${record.unit_price || 0}`
        };
      case 'settings':
        return {
          primary: record.companyName || 'Company Settings',
          secondary: record.email,
          badge: 'Settings',
          extra: record.phone
        };
      default:
        return {
          primary: record.name || record.id?.slice(0, 8) || 'Record',
          secondary: '',
          badge: '',
          extra: ''
        };
    }
  };

  // Export to CSV
  const exportToCSV = () => {
    if (filteredData.length === 0) {
      toast.error('No data to export');
      return;
    }

    // Get all unique keys
    const allKeys = new Set();
    filteredData.forEach(record => {
      Object.keys(record).forEach(key => allKeys.add(key));
    });
    const headers = Array.from(allKeys);

    // Create CSV content
    const csvContent = [
      headers.join(','),
      ...filteredData.map(record => 
        headers.map(header => {
          const value = record[header];
          if (value === null || value === undefined) return '';
          if (typeof value === 'object') return `"${JSON.stringify(value).replace(/"/g, '""')}"`;
          return `"${String(value).replace(/"/g, '""')}"`;
        }).join(',')
      )
    ].join('\n');

    // Download
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${activeCollection}_export.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Exported to CSV');
  };

  // Delete record
  const deleteRecord = async (recordId) => {
    if (!window.confirm('Are you sure you want to delete this record?')) return;
    
    try {
      const collection = collections.find(c => c.id === activeCollection);
      const response = await fetch(`${BACKEND_URL}${collection.endpoint}/${recordId}`, {
        method: 'DELETE'
      });
      if (response.ok) {
        toast.success('Record deleted');
        fetchData();
        setSelectedRecord(null);
      } else {
        toast.error('Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting:', error);
      toast.error('Error deleting record');
    }
  };

  // Save edited record
  const saveRecord = async () => {
    try {
      const collection = collections.find(c => c.id === activeCollection);
      const endpoint = collection.single 
        ? `${BACKEND_URL}${collection.endpoint}`
        : `${BACKEND_URL}${collection.endpoint}/${editingRecord.id}`;
      
      const response = await fetch(endpoint, {
        method: collection.single ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editedData)
      });
      
      if (response.ok) {
        toast.success('Record saved');
        setEditingRecord(null);
        fetchData();
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Error saving record');
    }
  };

  // Render record detail view
  const renderRecordDetail = (record) => {
    const isEditing = editingRecord?.id === record.id || (collections.find(c => c.id === activeCollection)?.single && editingRecord);
    const displayData = isEditing ? editedData : record;

    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between border-b pb-3">
          <h3 className="font-semibold text-lg">Record Details</h3>
          <div className="flex gap-2">
            {isEditing ? (
              <>
                <Button size="sm" variant="outline" onClick={() => setEditingRecord(null)}>
                  <X className="h-4 w-4 mr-1" />Cancel
                </Button>
                <Button size="sm" onClick={saveRecord} className="bg-green-600 text-white">
                  <Save className="h-4 w-4 mr-1" />Save
                </Button>
              </>
            ) : (
              <>
                <Button size="sm" variant="outline" onClick={() => {
                  setEditingRecord(record);
                  setEditedData({ ...record });
                }}>
                  <Edit2 className="h-4 w-4 mr-1" />Edit
                </Button>
                {!collections.find(c => c.id === activeCollection)?.single && (
                  <Button size="sm" variant="outline" className="text-red-600" onClick={() => deleteRecord(record.id)}>
                    <Trash2 className="h-4 w-4 mr-1" />Delete
                  </Button>
                )}
              </>
            )}
          </div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto space-y-3">
          {Object.entries(displayData).map(([key, value]) => {
            // Skip internal fields
            if (key === '_id') return null;
            
            const isObject = typeof value === 'object' && value !== null;
            const displayValue = isObject ? JSON.stringify(value, null, 2) : String(value ?? '');

            return (
              <div key={key} className="grid grid-cols-3 gap-2 items-start">
                <label className="text-sm font-medium text-gray-600 pt-2">{key}</label>
                <div className="col-span-2">
                  {isEditing ? (
                    isObject || displayValue.length > 100 ? (
                      <Textarea
                        value={typeof editedData[key] === 'object' ? JSON.stringify(editedData[key], null, 2) : (editedData[key] ?? '')}
                        onChange={(e) => {
                          let newValue = e.target.value;
                          // Try to parse JSON if it looks like JSON
                          if (newValue.startsWith('{') || newValue.startsWith('[')) {
                            try {
                              newValue = JSON.parse(newValue);
                            } catch {}
                          }
                          setEditedData({ ...editedData, [key]: newValue });
                        }}
                        rows={Math.min(10, displayValue.split('\n').length + 1)}
                        className="font-mono text-xs"
                      />
                    ) : (
                      <Input
                        value={editedData[key] ?? ''}
                        onChange={(e) => setEditedData({ ...editedData, [key]: e.target.value })}
                      />
                    )
                  ) : (
                    <div className={`text-sm ${isObject ? 'bg-gray-50 p-2 rounded font-mono text-xs whitespace-pre-wrap max-h-40 overflow-y-auto' : ''}`}>
                      {displayValue || <span className="text-gray-400 italic">empty</span>}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-gray-900 text-white p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="sm" className="text-white hover:bg-gray-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back
              </Button>
            </Link>
            <div className="flex items-center gap-2">
              <Database className="h-6 w-6 text-amber-500" />
              <h1 className="text-xl font-bold">Data Viewer</h1>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={fetchData} className="text-white border-gray-600 hover:bg-gray-800">
              <RefreshCw className="h-4 w-4 mr-1" />Refresh
            </Button>
            <Button size="sm" onClick={exportToCSV} className="bg-amber-500 text-white hover:bg-amber-600">
              <Download className="h-4 w-4 mr-1" />Export CSV
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-4">
        {/* Collection Tabs */}
        <div className="flex flex-wrap gap-2 mb-4">
          {collections.map(col => (
            <Button
              key={col.id}
              variant={activeCollection === col.id ? 'default' : 'outline'}
              onClick={() => setActiveCollection(col.id)}
              className={activeCollection === col.id ? `${col.color} text-white` : ''}
            >
              <col.icon className="h-4 w-4 mr-2" />
              {col.label}
            </Button>
          ))}
        </div>

        {/* Search */}
        <div className="mb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              value={searchQuery}
              onChange={(e) => { setSearchQuery(e.target.value); setCurrentPage(1); }}
              placeholder="Search records..."
              className="pl-10"
            />
          </div>
        </div>

        {/* Stats */}
        <div className="mb-4 text-sm text-gray-600">
          Showing {paginatedData.length} of {filteredData.length} records
          {searchQuery && ` (filtered from ${data.length} total)`}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
          {/* Records List */}
          <div className="lg:col-span-1">
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm flex items-center">
                  {React.createElement(collections.find(c => c.id === activeCollection)?.icon || Database, { className: 'h-4 w-4 mr-2' })}
                  {collections.find(c => c.id === activeCollection)?.label}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-8 text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500 mx-auto"></div>
                  </div>
                ) : paginatedData.length === 0 ? (
                  <div className="p-8 text-center text-gray-500">No records found</div>
                ) : (
                  <div className="divide-y max-h-[60vh] overflow-y-auto">
                    {paginatedData.map((record, idx) => {
                      const display = getDisplayFields(record);
                      const isSelected = selectedRecord?.id === record.id || (collections.find(c => c.id === activeCollection)?.single && selectedRecord);
                      
                      return (
                        <div
                          key={record.id || idx}
                          onClick={() => setSelectedRecord(record)}
                          className={`p-3 cursor-pointer hover:bg-gray-50 transition-colors ${isSelected ? 'bg-amber-50 border-l-4 border-amber-500' : ''}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-gray-900 truncate">{display.primary}</p>
                              <p className="text-xs text-gray-500 truncate">{display.secondary}</p>
                            </div>
                            <div className="flex items-center gap-2 ml-2">
                              {display.badge && (
                                <Badge variant="outline" className="text-xs whitespace-nowrap">
                                  {display.badge}
                                </Badge>
                              )}
                            </div>
                          </div>
                          {display.extra && (
                            <p className="text-xs text-gray-400 mt-1 truncate">{display.extra}</p>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between p-3 border-t">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-gray-600">
                      Page {currentPage} of {totalPages}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                      disabled={currentPage === totalPages}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Record Detail */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-4">
                {selectedRecord ? (
                  renderRecordDetail(selectedRecord)
                ) : (
                  <div className="text-center py-12 text-gray-500">
                    <Eye className="h-12 w-12 mx-auto mb-4 opacity-30" />
                    <p>Select a record to view details</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
};

export default DataViewer;
